import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  OnDestroy,
  signal,
  untracked,
} from '@angular/core';
import {
  ChevronDownIconComponent,
  FileDownloadIconComponent,
  FileIconComponent,
  InvoiceAttachmentIconComponent,
} from '@kuiper/icons';
import { TruncatePipe } from '../../../../ui-components/pipes/truncate/truncate.pipe';
import { DropdownOverlayDirective } from '../../directives/dropdown-overlay/dropdown-overlay.directive';
import {
  DROPDOWN_OVERLAY_PANEL_CLASS,
  normalizeDropdownPanelClass,
} from '../../helpers/dropdown-overlay/dropdown-overlay.helper';

export interface AttachmentPreviewItem {
  name: string;
  url: string;
}

@Component({
  selector: 'lib-attachment-preview',
  imports: [
    InvoiceAttachmentIconComponent,
    FileIconComponent,
    FileDownloadIconComponent,
    ChevronDownIconComponent,
    TruncatePipe,
    DropdownOverlayDirective,
  ],
  templateUrl: './attachment-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentPreviewComponent implements OnDestroy {
  /** Opaque id of the currently open attachment menu (not a component ref). */
  private static readonly activeMenuId = signal<symbol | null>(null);

  private readonly menuId = Symbol('attachment-preview-menu');

  attachments = input<AttachmentPreviewItem[]>([]);
  /** @deprecated Overlay handles positioning; kept for call-site compatibility. */
  containerRef = input<string>('');
  dropdownClass = input<string>('');
  label = input<string>('');

  readonly isMenuOpen = model(false);

  protected readonly panelClass = computed(() =>
    normalizeDropdownPanelClass([
      DROPDOWN_OVERLAY_PANEL_CLASS,
      this.dropdownClass(),
    ])
  );

  constructor() {
    // Claim / release the shared active slot when this menu opens or closes.
    effect(() => {
      if (this.isMenuOpen()) {
        AttachmentPreviewComponent.activeMenuId.set(this.menuId);
        return;
      }

      if (AttachmentPreviewComponent.activeMenuId() === this.menuId) {
        AttachmentPreviewComponent.activeMenuId.set(null);
      }
    });

    // Close this instance when another menu becomes active.
    // Read isMenuOpen via untracked so opening does not race the claim effect.
    effect(() => {
      const activeMenuId = AttachmentPreviewComponent.activeMenuId();
      if (
        activeMenuId !== this.menuId &&
        untracked(() => this.isMenuOpen())
      ) {
        this.isMenuOpen.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    if (AttachmentPreviewComponent.activeMenuId() === this.menuId) {
      AttachmentPreviewComponent.activeMenuId.set(null);
    }
  }
}
